import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import get from 'lodash/get'
import difference from 'lodash/difference'

import { Form, Select } from 'components/forms'
import Button from 'components/Button'
import { transformData, useQuery, useLazyQuery } from 'components/graphql'
import { Modal, AlertModal } from 'components/Modal'
import ViewerPDF from 'components/ViewerPDF'
import getQueryName from 'helpers/getNameOfGqlQuery'
import getCashRegistersTree from 'gql/query/cashRegistersTree'

import styles from '../../ClosingSection.module.scss'
import { getClosingsReport } from './gql'

export const ReportModal = ({ data, history, unmount, listQueryRef }) => {
	const [queryError, setQueryError] = useState()
	const fallbackUrl = useRef()
	const [useFallback, setUseFallback] = useState({})
	const [queryPagination, setQueryPagination] = useState({}) // { page, total }

	const queryName = useMemo(() => getQueryName(getClosingsReport), [getClosingsReport])
	const [getReport, { data: reportData, loading: reportLoading, variables, error }] = useLazyQuery(getClosingsReport, {
		onCompleted: (res) => {
			const { url, pagination } = res?.reports?.[queryName] || {}
			const { hasNext, cacheKey, expectedUrl } = pagination || {}
			if(expectedUrl) {
				fallbackUrl.current = expectedUrl
			}
			if(hasNext && cacheKey) {
				setQueryPagination({ ...queryPagination, page: queryPagination?.page + 1 })
				getReport({
					variables: { ...variables, pagination: { cacheKey } }
				})
			} else if(!url) {
				setQueryError(t('common.strings.site_error'))
			} else if(queryError) {
				setQueryError()
			}
		},
		onError: err => {
			// Handle gateway timeout. Reports service lambda continue executing because of longer timeout and probably pdf file can be uploaded to S3
			if(fallbackUrl.current) {
				setUseFallback({ checking: true })
				setTimeout(() => {
					fetch(fallbackUrl.current, { method: 'HEAD' })
						.then(res => {
							setUseFallback({ canUse: res.status === 200, checking: false })
							if(res.status !== 200) {
								setQueryError(t('server_errors.pdf_too_big'))
							} else if(queryError) {
								setQueryError()
							}
						})
						.catch(e => {
							setUseFallback({ checking: false })
							setQueryError('common.strings.site_error')
						})
				}, 30000)
			} else {
				setQueryPagination({})
				setQueryError(t('common.strings.site_error'))
			}
		}
	})
	const { data: treeData } = useQuery(getCashRegistersTree, { fetchPolicy: 'cache-first' })

	const mapTree = useCallback((items, parent, level = 0) => {
		if(!Array.isArray(items)) {
			return []
		}
		const out = items.reduce((outArr, item) => {
			let mappedChildren = []
			let outItem = { ...item }
			if(outItem.children) {
				outItem.leafIds = []
				const children = mapTree(outItem.children, outItem, level + 1)
				if(children.length) {
					mappedChildren = children
				}
				if(outItem.leafIds && parent) {
					parent.leafIds = [...parent.leafIds, ...outItem.leafIds]
				}
			}

			if(mappedChildren.length) {
				outItem.children = mappedChildren
				outArr.push({
					...outItem,
					children: mappedChildren
				})
			}

			// Cash register level
			if(level === 3) {
				outArr.push({
					...outItem,
					leafIds: [outItem.id]
				})
				parent.leafIds = [...parent.leafIds, outItem.id]
			}
			return outArr
		}, [])
		return out
	}, [])

	const [t] = useTranslation()

	const handleSubmit = ({ groupBy }) => {
		const { cashRegisterIds, period } = get(listQueryRef, 'current.variables.filter', {})
		const params = {
			search: get(listQueryRef, 'current.variables.params.search')
		}
		const mappedTree = mapTree(treeData?.children)
		const getChecked = items => {
			if(!Array.isArray(items)) {
				return []
			}
			const out = items.reduce((outArr, item) => {
				const isIncluded = item.leafIds ? !difference(item.leafIds, cashRegisterIds).length : false
				if(isIncluded) {
					outArr.push(item.name)
				} else {
					const nested = getChecked(item.children)
					return [...outArr, ...nested]
				}
				return outArr
			}, [])
			return out
		}

		const checkedItems = Array.isArray(cashRegisterIds) ? getChecked(mappedTree) : mappedTree.map(item => item?.name ?? '')
		const periodDiff = moment(period?.to).add(1, 'seconds').diff(period?.from, 'days')
		if(periodDiff > 1) {
			setQueryPagination({ page: 0, total: periodDiff + 1 }) // 1 extra query executed for generate pdf from data
		}
		getReport({
			variables: { cashRegisterIds, period, departments: checkedItems, ...groupBy && { groupBy }, ...params.search && { params } }
		})
	}

	if((reportData?.[queryName]?.url && !error) || useFallback?.canUse) {
		const url = reportData?.[queryName]?.url || fallbackUrl.current
		if(url) {
			return(
				<Modal fullScreen unmount={unmount} label={t('closing_section.strings.closing_report')} className={styles.closingReport}>
					<ViewerPDF src={url} />
				</Modal>
			)
		}
	}

	return (
		<Modal
			fullScreen
			unmount={unmount}
			label={t('closing_section.strings.closing_report')}
			className={styles.closingReport}
			{...(queryPagination.total || reportLoading) && { loadingProgress: Math.round(((queryPagination.page || 0.5) / queryPagination.total) * 100) }}
		>
			<Form onSubmit={handleSubmit} initialValues={{ groupBy: '' }}>
				<Select
					field='groupBy'
					label={t('closing_section.fields.formular_groupby')}
					simpleValue
					defaultValue={null}
					options={[
						{ value: '', label: t('closing_section.strings.formular_groupby_total') },
						{ value: 'date', label: t('closing_section.strings.formular_groupby_date') },
						{ value: 'shop', label: t('closing_section.strings.formular_groupby_shop') },
						{ value: 'dateShop', label: t('closing_section.strings.formular_groupby_shopdate') }
					]}
				/>
				<Button type='submit' status='primary' text={t('closing_section.buttons.closing_report')}/>
				{queryError &&
					<AlertModal
						onClose={() => setQueryError()}
						text={queryError}
					/>
				}
			</Form>
		</Modal>
	)
}


export const checkClosingsCount = (item, { listQueryRef }) => {
	// check if current closing list filter returned any closings we can generate report from
	const { data } = transformData(get(listQueryRef, 'current.data', {}), { queryFieldExtract: true })
	return !!get(data, 'items.length')
}
